import { useState, useEffect, useCallback, useRef } from 'react';
import { turfCutsApi, locationsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Scissors, Calendar, MapPin, Undo, Trash2, Eye, Navigation, Locate, Ruler, Camera, X, Upload, Image } from 'lucide-react';
import Map, { Source, Layer, NavigationControl, Marker } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

const MAP_STYLE = {
  version: 8,
  sources: { 'esri-satellite': { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: '&copy; Esri' } },
  layers: [{ id: 'esri-satellite', type: 'raster', source: 'esri-satellite', minzoom: 0, maxzoom: 19 }]
};

const FARM_CENTER = { longitude: 153.137, latitude: -27.943, zoom: 15 };
const POINT_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const POINT_COLORS = ['#2563EB', '#DC2626', '#16A34A', '#D97706', '#9333EA', '#0891B2', '#E11D48', '#65A30D'];
const getPointColor = (i) => POINT_COLORS[i % POINT_COLORS.length];

const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const calcPolygonArea = (points) => {
  if (points.length < 3) return 0;
  const refLat = points[0][1]; const refLng = points[0][0];
  const mLat = 111320; const mLng = 111320 * Math.cos(refLat * Math.PI / 180);
  const mp = points.map(p => [(p[0] - refLng) * mLng, (p[1] - refLat) * mLat]);
  let a = 0;
  for (let i = 0; i < mp.length; i++) { const j = (i + 1) % mp.length; a += mp[i][0] * mp[j][1]; a -= mp[j][0] * mp[i][1]; }
  return Math.abs(a) / 2;
};

// Lightweight cut preview (no MapLibre needed for list)
function CutPreview({ cut }) {
  const hasGps = cut.cut_points?.length > 0;
  const hasPhoto = cut.photos?.length > 0;
  
  if (hasPhoto) {
    return <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${cut.photos[0]}`} alt="Cut" className="w-full h-full object-cover" />;
  }
  
  // GPS points indicator
  return (
    <div className="w-full h-full bg-gradient-to-br from-green-800/80 to-green-900/90 flex flex-col items-center justify-center gap-1">
      {hasGps ? (
        <>
          <div className="flex gap-1">
            {cut.cut_points.slice(0, 5).map((_, i) => (
              <div key={`prev-${i}`} className="w-4 h-4 rounded-full border border-white/50 flex items-center justify-center text-white text-[8px] font-bold" style={{ backgroundColor: getPointColor(i) }}>
                {POINT_LABELS[i]}
              </div>
            ))}
            {cut.cut_points.length > 5 && <span className="text-white/70 text-[10px]">+{cut.cut_points.length - 5}</span>}
          </div>
          <span className="text-white/60 text-[10px]">{cut.cut_points.length} GPS points</span>
        </>
      ) : (
        <>
          <Scissors className="h-6 w-6 text-white/40" />
          <span className="text-white/50 text-[10px]">Manual entry</span>
        </>
      )}
    </div>
  );
}

export function TurfCutsPage() {
  const [turfCuts, setTurfCuts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCut, setSelectedCut] = useState(null);
  const [filterLocation, setFilterLocation] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Form state
  const [formLocationId, setFormLocationId] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formQuantity, setFormQuantity] = useState('');
  const [formQuality, setFormQuality] = useState('good');
  const [formNotes, setFormNotes] = useState('');

  // GPS points state (simplified A, B, C, D...)
  const [gpsPoints, setGpsPoints] = useState([]);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [autoCalc, setAutoCalc] = useState(true);

  // Photos
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [viewState, setViewState] = useState({ longitude: FARM_CENTER.longitude, latitude: FARM_CENTER.latitude, zoom: FARM_CENTER.zoom });

  const loadData = useCallback(async () => {
    try {
      const [cutsRes, locsRes] = await Promise.all([turfCutsApi.getAll(), locationsApi.getAll()]);
      setTurfCuts(cutsRes.data); setLocations(locsRes.data);
    } catch { toast.error('Failed to load data'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-calculate area from GPS points
  useEffect(() => {
    if (autoCalc && gpsPoints.length >= 2) {
      let area;
      if (gpsPoints.length >= 3) {
        area = calcPolygonArea(gpsPoints);
      } else {
        // 2 points = line, estimate area with default 10m width
        area = getDistanceMeters(gpsPoints[0][1], gpsPoints[0][0], gpsPoints[1][1], gpsPoints[1][0]) * 10;
      }
      setFormQuantity(area.toFixed(1));
    }
  }, [autoCalc, gpsPoints]);

  const selectedFormLocation = locations.find(l => l.id === formLocationId);

  const handleFieldChange = (locId) => {
    setFormLocationId(locId); setGpsPoints([]);
    const loc = locations.find(l => l.id === locId);
    if (loc?.coordinates?.length > 0) {
      const lngs = loc.coordinates.map(c => c[0]); const lats = loc.coordinates.map(c => c[1]);
      setViewState({ longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2, latitude: (Math.min(...lats) + Math.max(...lats)) / 2, zoom: 17 });
    }
  };

  const goToMyLocation = () => {
    if (!navigator.geolocation) { toast.error('GPS not available'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setViewState(prev => ({ ...prev, longitude: pos.coords.longitude, latitude: pos.coords.latitude, zoom: Math.max(prev.zoom, 18) })),
      () => toast.error('Could not get position'), { enableHighAccuracy: true }
    );
  };

  // Add a GPS point
  const addGpsPoint = () => {
    if (!navigator.geolocation) { toast.error('GPS not supported'); return; }
    if (!formLocationId) { toast.error('Select a field first'); return; }
    const label = POINT_LABELS[gpsPoints.length] || `P${gpsPoints.length + 1}`;
    toast.info(`Getting GPS for Point ${label}...`);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const pt = [pos.coords.longitude, pos.coords.latitude];
        setGpsPoints(prev => [...prev, pt]);
        setGpsAccuracy(Math.round(pos.coords.accuracy));
        setViewState(prev => ({ ...prev, longitude: pt[0], latitude: pt[1], zoom: Math.max(prev.zoom, 18) }));
        toast.success(`Point ${label} marked (${Math.round(pos.coords.accuracy)}m accuracy)`);
      },
      (err) => toast.error(err.code === 1 ? 'GPS permission denied' : 'Could not get GPS'),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // Tap map to add point
  const handleMapClick = useCallback((e) => {
    if (!formLocationId) return;
    setGpsPoints(prev => [...prev, [e.lngLat.lng, e.lngLat.lat]]);
  }, [formLocationId]);

  const handleAddTurfCut = async (e) => {
    e.preventDefault();
    if (!formLocationId) { toast.error('Please select a location'); return; }
    if (!formQuantity || parseFloat(formQuantity) <= 0) { toast.error('Enter a valid quantity'); return; }

    try {
      setUploading(true);
      const cutRes = await turfCutsApi.create({
        location_id: formLocationId, date: formDate,
        quantity_sqm: parseFloat(formQuantity), quality: formQuality,
        cut_points: gpsPoints, notes: formNotes || null,
      });

      // Upload photos if any
      if (photoFiles.length > 0) {
        for (const file of photoFiles) {
          const fd = new FormData();
          fd.append('file', file);
          try { await turfCutsApi.uploadPhoto(cutRes.data.id, fd); } catch (err) { console.error('Photo upload failed:', err); }
        }
      }

      toast.success('Turf cut recorded');
      setDialogOpen(false); resetForm(); loadData();
    } catch { toast.error('Failed to record turf cut'); } finally { setUploading(false); }
  };

  const resetForm = () => {
    setFormLocationId(''); setFormDate(new Date().toISOString().split('T')[0]);
    setFormQuantity(''); setFormQuality('good'); setFormNotes('');
    setGpsPoints([]); setGpsAccuracy(null); setAutoCalc(true);
    setPhotoFiles([]);
  };

  const openDialog = () => { resetForm(); setDialogOpen(true); };
  const openDetail = (cut) => { setSelectedCut(cut); setDetailOpen(true); };

  const getDetailViewState = (cut) => {
    const pts = cut.cut_points || [];
    const loc = locations.find(l => l.id === cut.location_id);
    const allPts = pts.length > 0 ? pts : (loc?.coordinates || []);
    if (allPts.length === 0) return { longitude: FARM_CENTER.longitude, latitude: FARM_CENTER.latitude, zoom: FARM_CENTER.zoom };
    const lngs = allPts.map(p => p[0]); const lats = allPts.map(p => p[1]);
    return { longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2, latitude: (Math.min(...lats) + Math.max(...lats)) / 2, zoom: 17 };
  };

  const getLocationBoundary = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    return loc?.coordinates?.length >= 3 ? loc.coordinates : null;
  };

  const filteredCuts = turfCuts.filter(cut => {
    if (filterLocation !== 'all' && cut.location_id !== filterLocation) return false;
    if (startDate && cut.date < startDate) return false;
    if (endDate && cut.date > endDate) return false;
    return true;
  });

  const totalSqm = filteredCuts.reduce((sum, cut) => sum + cut.quantity_sqm, 0);
  const clearFilters = () => { setFilterLocation('all'); setStartDate(''); setEndDate(''); };
  const calcArea = gpsPoints.length >= 3 ? calcPolygonArea(gpsPoints) : (gpsPoints.length === 2 ? getDistanceMeters(gpsPoints[0][1], gpsPoints[0][0], gpsPoints[1][1], gpsPoints[1][0]) * 10 : 0);

  if (loading) return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="turf-cuts-loading" />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="turf-cuts-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Turf Cuts</h1>
          <p className="text-muted-foreground">Track harvesting with GPS points and photos</p>
        </div>
        <Button className="btn-primary" onClick={openDialog} data-testid="add-turf-cut-btn">
          <Plus className="h-4 w-4 mr-2" /> Record Turf Cut
        </Button>
      </div>

      {/* Filters */}
      <Card><CardContent className="pt-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label>Location</Label>
            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger className="w-[180px]" data-testid="turf-cuts-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="turf-cuts-start-date" /></div>
          <div className="space-y-2"><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="turf-cuts-end-date" /></div>
          <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">Clear Filters</Button>
        </div>
      </CardContent></Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="stat-card"><CardContent className="pt-6"><div className="flex items-center gap-4"><Scissors className="h-10 w-10 text-primary" /><div><p className="text-sm text-muted-foreground">Total Records</p><p className="text-2xl font-heading font-bold">{filteredCuts.length}</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="pt-6"><div className="flex items-center gap-4"><Calendar className="h-10 w-10 text-primary" /><div><p className="text-sm text-muted-foreground">Total Harvested</p><p className="text-2xl font-heading font-bold">{totalSqm.toLocaleString()} sqm</p></div></div></CardContent></Card>
      </div>

      {/* Cuts List with Map Thumbnails */}
      <Card data-testid="turf-cuts-table">
        <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Scissors className="h-5 w-5 text-primary" /> Harvest Records</CardTitle></CardHeader>
        <CardContent>
          {locations.length === 0 ? (<p className="text-muted-foreground text-center py-8">Add locations first before recording turf cuts</p>
          ) : filteredCuts.length === 0 ? (<p className="text-muted-foreground text-center py-8">No turf cuts recorded yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredCuts.map((cut) => (
                <div key={cut.id} onClick={() => openDetail(cut)} className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow group" data-testid={`turf-cut-card-${cut.id}`}>
                  {/* Map thumbnail or photo */}
                  <div className="h-32 relative">
                    {cut.photos?.length > 0 ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${cut.photos[0]}`} alt="Cut" className="w-full h-full object-cover" />
                    ) : (
                      <CutPreview cut={cut} />
                    )}
                    <div className="absolute top-2 right-2">
                      <Badge className={`quality-${cut.quality} text-[10px]`}>{cut.quality}</Badge>
                    </div>
                    {cut.photos?.length > 1 && (
                      <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Camera className="h-2.5 w-2.5" /> {cut.photos.length}
                      </div>
                    )}
                    {cut.cut_points?.length > 0 && (
                      <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
                        <Navigation className="h-2.5 w-2.5" /> {cut.cut_points.length} pts
                      </div>
                    )}
                  </div>
                  {/* Card info */}
                  <div className="p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-heading font-semibold text-sm">{cut.location_name || 'Unknown'}</span>
                      <span className="text-xs text-muted-foreground">{cut.date}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-primary">{cut.quantity_sqm.toLocaleString()} sqm</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); openDetail(cut); }}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {cut.notes && <p className="text-xs text-muted-foreground truncate">{cut.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Turf Cut Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto" data-testid="add-turf-cut-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Record Turf Cut</DialogTitle></DialogHeader>
          <form onSubmit={handleAddTurfCut} className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left - Form fields */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Field *</Label>
                  <Select value={formLocationId || "none"} onValueChange={(v) => handleFieldChange(v === "none" ? '' : v)}>
                    <SelectTrigger data-testid="turf-cut-location-select"><SelectValue placeholder="Select field" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select field...</SelectItem>
                      {locations.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.name} {loc.area_hectares ? `(${loc.area_hectares} ha)` : ''}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Date *</Label><Input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} required data-testid="turf-cut-date-input" /></div>
                  <div className="space-y-2"><Label>Quality *</Label>
                    <Select value={formQuality} onValueChange={setFormQuality}><SelectTrigger data-testid="turf-cut-quality-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="excellent">Excellent</SelectItem><SelectItem value="good">Good</SelectItem><SelectItem value="fair">Fair</SelectItem><SelectItem value="poor">Poor</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>

                {/* GPS Points */}
                <Card className="border-blue-300 bg-blue-50/50">
                  <CardContent className="pt-4 pb-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Navigation className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-800">GPS Points ({gpsPoints.length})</span>
                      </div>
                      <Button type="button" size="sm" variant="ghost" onClick={goToMyLocation} className="text-xs h-6" data-testid="my-location-btn">
                        <Locate className="h-3 w-3 mr-1" /> My Location
                      </Button>
                    </div>

                    <p className="text-xs text-blue-700">Mark points to outline the cut area. Use GPS button or tap the map.</p>

                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={addGpsPoint}
                        className="flex-1 text-xs h-9 bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-gps-point-btn"
                        disabled={!formLocationId}>
                        <Locate className="h-3.5 w-3.5 mr-1.5" /> Add Point {POINT_LABELS[gpsPoints.length] || (gpsPoints.length + 1)}
                      </Button>
                      {gpsPoints.length > 0 && (
                        <Button type="button" size="sm" variant="outline" onClick={() => setGpsPoints(prev => prev.slice(0, -1))} className="text-xs h-9" data-testid="undo-point-btn">
                          <Undo className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {gpsPoints.length > 0 && (
                        <Button type="button" size="sm" variant="outline" onClick={() => setGpsPoints([])} className="text-xs h-9 text-destructive" data-testid="clear-points-btn">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>

                    {gpsAccuracy && <p className="text-[10px] text-blue-500">Last accuracy: {gpsAccuracy}m</p>}

                    {/* Points list */}
                    {gpsPoints.length > 0 && (
                      <div className="space-y-1 max-h-[100px] overflow-y-auto">
                        {gpsPoints.map((pt, i) => (
                          <div key={`gps-pt-${i}-${pt[0]}`} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border">
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: getPointColor(i) }}>
                                {POINT_LABELS[i] || i + 1}
                              </span>
                              <span className="font-mono text-[10px]">{pt[1].toFixed(5)}, {pt[0].toFixed(5)}</span>
                            </div>
                            <Button type="button" size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => setGpsPoints(prev => prev.filter((_, idx) => idx !== i))}>
                              <X className="h-3 w-3 text-muted-foreground" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Auto-calculated area */}
                    {gpsPoints.length >= 2 && (
                      <div className="p-2 bg-blue-100 rounded-md text-center">
                        <p className="text-sm font-bold text-blue-900">{calcArea.toFixed(1)} sqm</p>
                        <p className="text-[10px] text-blue-700">
                          {gpsPoints.length >= 3 ? `Polygon area (${gpsPoints.length} points)` : `Estimated (line x 10m width)`}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quantity */}
                <div className="space-y-2">
                  <Label>Quantity (sqm) *{autoCalc && gpsPoints.length >= 2 ? ' (auto-calculated — edit to override)' : ''}</Label>
                  <Input type="number" step="0.01" value={formQuantity}
                    onChange={(e) => { setFormQuantity(e.target.value); setAutoCalc(false); }}
                    required placeholder="e.g. 500" data-testid="turf-cut-quantity-input" />
                </div>

                {/* Photos */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Camera className="h-4 w-4" /> Photos</Label>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-accent text-sm">
                      <Upload className="h-4 w-4" /> Upload
                      <input type="file" accept="image/*" multiple className="hidden" data-testid="photo-upload-input"
                        onChange={(e) => setPhotoFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                    </label>
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-accent text-sm">
                      <Camera className="h-4 w-4" /> Take Photo
                      <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="photo-capture-input"
                        onChange={(e) => setPhotoFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                    </label>
                  </div>
                  {photoFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {photoFiles.map((file, idx) => (
                        <div key={`photo-${file.name}-${idx}`} className="relative group">
                          <img src={URL.createObjectURL(file)} alt="" className="w-16 h-16 rounded-md object-cover border" />
                          <button type="button" className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => setPhotoFiles(prev => prev.filter((_, i) => i !== idx))}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="Any observations..." data-testid="turf-cut-notes-input" />
                </div>
              </div>

              {/* Right - Map */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Tap map or use GPS to add points</Label>
                </div>
                <div className="h-[300px] md:h-[450px] rounded-lg overflow-hidden border relative" data-testid="turf-cut-map">
                  <Map {...viewState} onMove={(e) => setViewState(e.viewState)} onClick={handleMapClick}
                    mapStyle={MAP_STYLE} style={{ width: '100%', height: '100%' }}
                    cursor={formLocationId ? 'crosshair' : 'grab'}>
                    <NavigationControl position="top-right" />

                    {/* Field boundary */}
                    {selectedFormLocation?.coordinates?.length >= 3 && (
                      <Source id="field-boundary" type="geojson" data={{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...selectedFormLocation.coordinates, selectedFormLocation.coordinates[0]]] } }}>
                        <Layer id="field-fill" type="fill" paint={{ 'fill-color': '#1B4332', 'fill-opacity': 0.1 }} />
                        <Layer id="field-line" type="line" paint={{ 'line-color': '#EF4444', 'line-width': 2, 'line-dasharray': [4, 4] }} />
                      </Source>
                    )}

                    {/* Lines between GPS points */}
                    {gpsPoints.length >= 2 && (
                      <Source id="points-line" type="geojson" data={{ type: 'Feature', geometry: { type: 'LineString', coordinates: [...gpsPoints, gpsPoints[0]] } }}>
                        <Layer id="points-line-layer" type="line" paint={{ 'line-color': '#2563EB', 'line-width': 2.5, 'line-dasharray': [4, 2] }} />
                      </Source>
                    )}

                    {/* Polygon fill when 3+ points */}
                    {gpsPoints.length >= 3 && (
                      <Source id="points-poly" type="geojson" data={{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...gpsPoints, gpsPoints[0]]] } }}>
                        <Layer id="points-poly-fill" type="fill" paint={{ 'fill-color': '#2563EB', 'fill-opacity': 0.2 }} />
                      </Source>
                    )}

                    {/* Point markers with labels */}
                    {gpsPoints.map((pt, idx) => (
                      <Marker key={`pt-${idx}-${pt[0]}`} longitude={pt[0]} latitude={pt[1]}>
                        <div className="w-7 h-7 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: getPointColor(idx) }}>
                          {POINT_LABELS[idx] || idx + 1}
                        </div>
                      </Marker>
                    ))}
                  </Map>
                </div>

                {gpsPoints.length === 0 && formLocationId && (
                  <p className="text-xs text-muted-foreground">Use "Add Point" button for GPS, or tap directly on the map</p>
                )}
                {gpsPoints.length === 1 && (
                  <p className="text-xs text-blue-600">Point A marked. Add Point B to define the cut line.</p>
                )}
                {gpsPoints.length >= 2 && (
                  <p className="text-xs text-blue-700 font-medium">
                    {gpsPoints.length} points = ~{calcArea.toFixed(0)} sqm. Add more points for accuracy or submit.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="turf-cut-submit-btn" disabled={!formLocationId || !formQuantity || uploading}>
                {uploading ? 'Saving...' : 'Record Cut'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto" data-testid="turf-cut-detail-dialog">
          {selectedCut && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-3">
                  <Scissors className="h-5 w-5 text-primary" /> Turf Cut - {selectedCut.date}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Field</p>
                    <p className="font-medium" data-testid="detail-cut-field">{selectedCut.location_name || 'Unknown'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Quantity</p>
                    <p className="font-medium font-mono" data-testid="detail-cut-qty">{selectedCut.quantity_sqm.toLocaleString()} sqm</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Quality</p>
                    <Badge className={`quality-${selectedCut.quality}`} data-testid="detail-cut-quality">{selectedCut.quality}</Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">GPS</p>
                    {selectedCut.cut_points?.length > 0 ? (
                      <Badge variant="outline" className="text-xs"><Navigation className="h-3 w-3 mr-1" />{selectedCut.cut_points.length} points</Badge>
                    ) : (<p className="text-muted-foreground text-sm">Manual entry</p>)}
                  </div>
                </div>

                {/* Photos */}
                {selectedCut.photos?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Image className="h-3 w-3" /> Photos ({selectedCut.photos.length})</p>
                    <div className="flex flex-wrap gap-3">
                      {selectedCut.photos.map((photo) => (
                        <a key={`detail-photo-${photo}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo}`} alt="Cut photo"
                            className="w-28 h-28 rounded-lg object-cover border hover:opacity-80 transition-opacity cursor-pointer" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* GPS Points */}
                {selectedCut.cut_points?.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">GPS Points</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCut.cut_points.map((pt, i) => (
                        <span key={`detail-pt-${i}`} className="inline-flex items-center gap-1.5 text-xs bg-blue-50 border border-blue-200 rounded-full px-2 py-1">
                          <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: getPointColor(i) }}>{POINT_LABELS[i]}</span>
                          <span className="font-mono text-[10px]">{pt[1].toFixed(5)}, {pt[0].toFixed(5)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedCut.notes && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Notes</p>
                    <p className="bg-secondary/20 p-3 rounded-lg border">{selectedCut.notes}</p>
                  </div>
                )}

                {/* Map */}
                {(selectedCut.cut_points?.length > 0 || getLocationBoundary(selectedCut.location_id)) && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Cut Location</p>
                    <div className="h-[300px] rounded-lg overflow-hidden border" data-testid="detail-cut-map">
                      <Map initialViewState={getDetailViewState(selectedCut)} mapStyle={MAP_STYLE} style={{ width: '100%', height: '100%' }}>
                        <NavigationControl position="top-right" />
                        {getLocationBoundary(selectedCut.location_id) && (
                          <Source id="detail-field" type="geojson" data={{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...getLocationBoundary(selectedCut.location_id), getLocationBoundary(selectedCut.location_id)[0]]] } }}>
                            <Layer id="detail-field-line" type="line" paint={{ 'line-color': '#EF4444', 'line-width': 2, 'line-dasharray': [4, 4] }} />
                          </Source>
                        )}
                        {selectedCut.cut_points?.length >= 2 && (
                          <Source id="detail-line" type="geojson" data={{ type: 'Feature', geometry: { type: 'LineString', coordinates: [...selectedCut.cut_points, selectedCut.cut_points[0]] } }}>
                            <Layer id="detail-line-layer" type="line" paint={{ 'line-color': '#2563EB', 'line-width': 2.5 }} />
                          </Source>
                        )}
                        {selectedCut.cut_points?.length >= 3 && (
                          <Source id="detail-poly" type="geojson" data={{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...selectedCut.cut_points, selectedCut.cut_points[0]]] } }}>
                            <Layer id="detail-poly-fill" type="fill" paint={{ 'fill-color': '#2563EB', 'fill-opacity': 0.25 }} />
                          </Source>
                        )}
                        {selectedCut.cut_points?.map((pt, i) => (
                          <Marker key={`detail-marker-${i}`} longitude={pt[0]} latitude={pt[1]}>
                            <div className="w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: getPointColor(i) }}>
                              {POINT_LABELS[i]}
                            </div>
                          </Marker>
                        ))}
                      </Map>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
